import { getDistanceFromLatLonInM, interpolate, findNearestPointOnTrack } from './geometry';

export interface Anchor {
    pk: number;
    coord: [number, number]; // [lat, lon]
}

export class TrackReferenceSystem {
    public lineName: 'Centrale' | 'Balagne';
    private anchors: Anchor[] = [];
    private path: [number, number][]; // [lat, lon]
    private pathDistances: number[] = [];

    constructor(initialAnchors: Anchor[], rawPath: [number, number][], lineName: 'Centrale' | 'Balagne') {
        this.lineName = lineName;
        // Convert rawPath [lon, lat] to [lat, lon]
        this.path = rawPath.map(c => [c[1], c[0]]);

        // Calculate cumulative distances along the track
        let totalDist = 0;
        this.pathDistances = [0];
        if (this.path.length > 0) {
            for (let i = 0; i < this.path.length - 1; i++) {
                totalDist += getDistanceFromLatLonInM(
                    this.path[i][0], this.path[i][1],
                    this.path[i + 1][0], this.path[i + 1][1]
                );
                this.pathDistances.push(totalDist);
            }
        }

        this.setAnchors(initialAnchors);
    }

    public setAnchors(anchors: Anchor[]) {
        // Sort anchors by PK descending (since PKs decrease from Calvi towards Reginu)
        this.anchors = [...anchors].sort((a, b) => b.pk - a.pk);
    }

    /**
     * Gets the geometric distance of a coordinate along the track from the start (Calvi).
     */
    private getTrackDistanceAtCoord(coord: [number, number]): number {
        if (this.path.length === 0) return 0;
        const nearest = findNearestPointOnTrack(coord, this.path);
        const pathDist = this.pathDistances[nearest.index] || 0;
        const pt = this.path[nearest.index];
        if (!pt) return 0;
        return pathDist +
            getDistanceFromLatLonInM(pt[0], pt[1], nearest.point[0], nearest.point[1]);
    }

    /**
     * Maps a PK to its geographic coordinate using piecewise linear interpolation.
     */
    public pkToCoord(targetPK: number): [number, number] | null {
        if (this.anchors.length < 2 || this.path.length === 0 || this.pathDistances.length === 0) return null;

        // Find surrounding anchors
        let upper: Anchor | null = null;
        let lower: Anchor | null = null;

        for (let i = 0; i < this.anchors.length - 1; i++) {
            if (targetPK <= this.anchors[i].pk && targetPK >= this.anchors[i + 1].pk) {
                upper = this.anchors[i];
                lower = this.anchors[i + 1];
                break;
            }
        }

        if (!upper || !lower) {
            // Extrapolation or out of bounds
            if (targetPK > this.anchors[0].pk) {
                upper = this.anchors[0];
                lower = this.anchors[1];
            } else {
                upper = this.anchors[this.anchors.length - 2];
                lower = this.anchors[this.anchors.length - 1];
            }
        }

        const distUpper = this.getTrackDistanceAtCoord(upper.coord);
        const distLower = this.getTrackDistanceAtCoord(lower.coord);

        // Fraction of PK between anchors
        const pkDiff = upper.pk - lower.pk;
        const pkFraction = pkDiff === 0 ? 0 : (upper.pk - targetPK) / pkDiff;
        // Interpolated distance along track
        const targetDist = distUpper + (distLower - distUpper) * pkFraction;

        // Extrapolate before beginning of track
        if (targetDist < 0) {
            const p1 = this.path[0];
            const p2 = this.path[1];
            const segLen = this.pathDistances[1];
            if (segLen === 0) return p1;
            const frac = targetDist / segLen;
            return interpolate(p1, p2, frac);
        }

        // Find points in path corresponding to this distance
        let idx = 0;
        while (idx < this.pathDistances.length - 1 && this.pathDistances[idx + 1] < targetDist) {
            idx++;
        }

        // Extrapolate past end of track
        if (idx >= this.path.length - 1) {
            const p1 = this.path[this.path.length - 2];
            const p2 = this.path[this.path.length - 1];
            const segLen = this.pathDistances[this.pathDistances.length - 1] - this.pathDistances[this.pathDistances.length - 2];
            if (segLen === 0) return p2;
            const distPastEnd = targetDist - this.pathDistances[this.pathDistances.length - 1];
            const frac = 1 + (distPastEnd / segLen);
            return interpolate(p1, p2, frac);
        }

        const dCurrent = this.pathDistances[idx];
        const dNext = this.pathDistances[idx + 1];

        // Prevent division by zero
        if (dNext === dCurrent) return this.path[idx];

        const segFraction = (targetDist - dCurrent) / (dNext - dCurrent);

        return interpolate(this.path[idx], this.path[idx + 1], segFraction);
    }

    /**
     * Maps a geographic coordinate to its PK.
     */
    public coordToPK(coord: [number, number]): number {
        if (this.anchors.length === 0) return 0;
        if (this.anchors.length === 1) return this.anchors[0].pk;

        const targetDist = this.getTrackDistanceAtCoord(coord);

        // Find surrounding anchors by track distance
        let upper: Anchor | null = null;
        let lower: Anchor | null = null;

        const anchorDistances = this.anchors.map(a => this.getTrackDistanceAtCoord(a.coord));
        const lastIdx = anchorDistances.length - 1;
        const isAscending = anchorDistances[0] < anchorDistances[lastIdx];

        for (let i = 0; i < anchorDistances.length - 1; i++) {
            const d1 = anchorDistances[i];
            const d2 = anchorDistances[i + 1];
            const minD = Math.min(d1, d2);
            const maxD = Math.max(d1, d2);

            if (targetDist >= minD && targetDist <= maxD) {
                upper = this.anchors[i];
                lower = this.anchors[i + 1];
                const dUpper = d1;
                const dLower = d2;

                // Si les distances sont égales pour éviter une division par zéro
                if (dLower === dUpper) return upper.pk;

                const distFraction = (targetDist - dUpper) / (dLower - dUpper);
                return upper.pk - (upper.pk - lower.pk) * distFraction;
            }
        }

        // Extrapolation en dehors des ancres définies
        if (isAscending) {
            if (targetDist < anchorDistances[0]) {
                const ratio = (this.anchors[0].pk - this.anchors[1].pk) / (anchorDistances[1] - anchorDistances[0]);
                return this.anchors[0].pk + (anchorDistances[0] - targetDist) * ratio;
            } else {
                const ratio = (this.anchors[lastIdx - 1].pk - this.anchors[lastIdx].pk) / (anchorDistances[lastIdx] - anchorDistances[lastIdx - 1]);
                return this.anchors[lastIdx].pk - (targetDist - anchorDistances[lastIdx]) * ratio;
            }
        } else {
            // Cas descendant (les distances diminuent à mesure que les PK augmentent, ou inversement)
            if (targetDist > anchorDistances[0]) {
                const ratio = (this.anchors[0].pk - this.anchors[1].pk) / (anchorDistances[1] - anchorDistances[0]);
                return this.anchors[0].pk + (anchorDistances[0] - targetDist) * ratio;
            } else {
                const ratio = (this.anchors[lastIdx - 1].pk - this.anchors[lastIdx].pk) / (anchorDistances[lastIdx] - anchorDistances[lastIdx - 1]);
                return this.anchors[lastIdx].pk - (targetDist - anchorDistances[lastIdx]) * ratio;
            }
        }
    }

    /**
     * Maps a PK range to the exact sequence of raw path coordinates between them.
     */
    public pkToPath(pk1: number, pk2: number): [number, number][] {
        if (this.path.length === 0) return [];

        const p1 = this.pkToCoord(pk1);
        const p2 = this.pkToCoord(pk2);

        if (!p1 || !p2) return [];

        const dist1 = this.getTrackDistanceAtCoord(p1);
        const dist2 = this.getTrackDistanceAtCoord(p2);

        const minDist = Math.min(dist1, dist2);
        const maxDist = Math.max(dist1, dist2);

        const segment: [number, number][] = [];

        // Add the exact start point
        segment.push(dist1 < dist2 ? p1 : p2);

        // Add all intermediate original track points
        for (let i = 0; i < this.pathDistances.length; i++) {
            const d = this.pathDistances[i];
            if (d > minDist && d < maxDist) {
                segment.push(this.path[i]);
            }
        }

        // Add the exact end point
        segment.push(dist1 > dist2 ? p1 : p2);

        // If the original request had pk1 < pk2, they expect the array to flow in that direction geographically. 
        // We know distance 0 is Calvi, and PKs *decrease* from Calvi to Ile Rousse.
        // Therefore, higher distance = lower PK.
        // If they requested a range from a lower PK to a higher PK, they are asking to go backwards towards Calvi.
        if (pk1 > pk2) {
            segment.reverse();
        }

        return segment;
    }

    /**
     * Gets the geographic distance from a coordinate to the nearest point on the track.
     */
    public getDistanceToTrack(coord: [number, number]): number {
        if (this.path.length === 0) return Infinity;
        const nearest = findNearestPointOnTrack(coord, this.path);
        return nearest.distance;
    }
}
